<template>
	<a-modal
		class="g-header-info-card-edit-modal"
		:visible="visible"
		title="头部模块编辑"
		width="674px"
		:centered="true"
		:mask-closable="false"
		:destroy-on-close="true"
		@cancel="onCancel"
	>
		<div class="g-header-info-card-edit">
			<div class="g-header-info-card-edit__preview" @click.stop>
				<div class="g-header-info-card-edit__cover" :style="coverWrapStyle">
					<UploadProgressPanel
						v-if="isUploading"
						:tasks="uploadTasks"
						variant="item"
						hide-filename
					/>
					<template v-else>
						<img
							v-if="coverUrl"
							:src="coverUrl"
							class="g-header-info-card-edit__cover-img"
							alt=""
						/>
						<i v-else class="mbm-iconfont mbm-tupian g-header-info-card-edit__cover-empty"></i>
					</template>
				</div>
				<div class="g-header-info-card-edit__text">
					<input
						v-model="form.title"
						type="text"
						class="g-header-info-card-edit__title-input"
						:style="{ color: form.titleColor }"
						maxlength="40"
						placeholder="输入标题"
					/>
					<input
						v-model="form.subtitle"
						type="text"
						class="g-header-info-card-edit__subtitle-input"
						:style="{ color: form.subtitleColor }"
						maxlength="60"
						placeholder="输入副标题"
					/>
				</div>
			</div>

			<div class="g-header-info-card-edit__panel" @click.stop>
				<div class="g-header-info-card-edit__link-row">
					<i class="mbm-iconfont mbm-lianjie"></i>
					<span :class="{ 'is-empty': !jumpLinkLabel }">{{ jumpLinkLabel || '未设置链接' }}</span>
				</div>
				<div class="g-header-info-card-edit__toolbar">
					<button type="button" class="g-header-info-card-edit__action" @click="triggerImageSelect">换图</button>
					<button type="button" class="g-header-info-card-edit__action" @click="jumpLinkVisible = true">换链接</button>
					<div class="g-header-info-card-edit__colors">
						<ColorPicker
							v-model="form.borderColor"
							title="图片外边框颜色"
							@change="onBorderColorInput"
						/>
						<ColorPicker
							v-model="form.titleColor"
							title="标题颜色"
							@change="onTitleColorInput"
						/>
						<ColorPicker
							v-model="form.subtitleColor"
							title="副标题颜色"
							@change="onSubtitleColorInput"
						/>
					</div>
				</div>
			</div>
		</div>

		<template #footer>
			<button type="button" class="g-header-info-card-edit__footer-btn" @click="onCancel">取消</button>
			<button type="button" class="g-header-info-card-edit__footer-btn is-confirm" :disabled="isUploading" @click="onConfirm">确定</button>
		</template>

		<input
			ref="fileInput"
			type="file"
			accept="image/*"
			class="g-header-info-card-edit__file-input"
			@change="onFileChange"
		/>

		<JumpLink
			:visible="jumpLinkVisible"
			:jumpLink="form.jumpLink || emptyJumpLink"
			:linkTypes="['qrcode']"
			@submit="onJumpLinkSubmit"
			@cancel="jumpLinkVisible = false"
		/>
	</a-modal>
</template>

<script>
	import JumpLink from '@/components/jumpLink.vue';
	import ColorPicker from './ColorPicker.vue';
	import { getSwatchBorderColor, normalizeHexColor } from '../utils/richText.js';
	import {
		filterValidLocalFiles,
		revokeLocalImageUrl,
	} from '../utils/localImage.js';
	import UploadProgressPanel from './UploadProgressPanel.vue';
	import {
		createUploadTasks,
		updateUploadTaskProgress,
		uploadHeaderMediaFiles,
	} from '../utils/uploadHeaderMedia.js';
	import { createEmptyJumpLink, formatJumpLinkLabel } from '../utils/jumpLink.js';
	import {
		getHeaderInfoCardDefaults,
		getHeaderInfoCardImageUrl,
		normalizeHeaderInfoCardData,
	} from '../utils/headerInfoCard.js';

	export default {
		name: 'GHeaderInfoCardEditModal',
		components: { JumpLink, UploadProgressPanel, ColorPicker },
		props: {
			visible: {
				type: Boolean,
				default: false,
			},
			value: {
				type: Object,
				default: () => ({}),
			},
		},
		emits: ['update:visible', 'confirm', 'cancel'],
		data() {
			return {
				form: getHeaderInfoCardDefaults(),
				jumpLinkVisible: false,
				emptyJumpLink: createEmptyJumpLink(),
				isUploading: false,
				uploadTasks: [],
			};
		},
		computed: {
			coverUrl() {
				return getHeaderInfoCardImageUrl(this.form.image);
			},
			coverWrapStyle() {
				return {
					width: `${this.form.imageSize}px`,
					height: `${this.form.imageSize}px`,
					borderWidth: `${this.form.borderWidth}px`,
					borderColor: this.form.borderColor,
					borderRadius: `${this.form.imageRadius}px`,
				};
			},
			jumpLinkLabel() {
				return formatJumpLinkLabel(this.form.jumpLink);
			},
		},
		watch: {
			visible(val) {
				if (val) {
					this.syncForm();
				}
			},
			value: {
				deep: true,
				handler() {
					if (this.visible) {
						this.syncForm();
					}
				},
			},
		},
		methods: {
			syncForm() {
				this.form = normalizeHeaderInfoCardData(this.value);
			},
			normalizeColor(color) {
				return normalizeHexColor(color);
			},
			getSwatchStyle(color) {
				const normalized = normalizeHexColor(color);
				return {
					background: normalized,
					borderColor: getSwatchBorderColor(normalized),
				};
			},
			triggerImageSelect() {
				this.$refs.fileInput?.click();
			},
			async onFileChange(event) {
				const file = (event.target.files || [])[0];
				event.target.value = '';
				const validFiles = filterValidLocalFiles(file ? [file] : []);
				if (!validFiles.length) return;
				this.isUploading = true;
				this.uploadTasks = createUploadTasks([validFiles[0]]);
				try {
					const [item] = await uploadHeaderMediaFiles(this, {
						type: 'image',
						files: [validFiles[0]],
						onProgress: payload => updateUploadTaskProgress(this.uploadTasks, payload),
					});
					if (this.form.image?.local) {
						revokeLocalImageUrl(this.form.image.previewUrl);
					}
					this.form.image = item;
				} catch (error) {
					this.$message?.error?.('上传失败');
				} finally {
					this.isUploading = false;
					this.uploadTasks = [];
				}
			},
			onBorderColorInput(color) {
				this.form.borderColor = color;
			},
			onTitleColorInput(color) {
				this.form.titleColor = color;
			},
			onSubtitleColorInput(color) {
				this.form.subtitleColor = color;
			},
			onJumpLinkSubmit(jumpLink) {
				this.form.jumpLink = jumpLink?.type ? { ...jumpLink } : null;
				this.jumpLinkVisible = false;
			},
			onCancel() {
				this.$emit('update:visible', false);
				this.$emit('cancel');
			},
			onConfirm() {
				this.$emit('confirm', normalizeHeaderInfoCardData(this.form));
				this.$emit('update:visible', false);
			},
		},
	};
</script>

<style scoped lang="less">
	.g-header-info-card-edit {
		padding: 4px 0 12px;
	}

	.g-header-info-card-edit__preview {
		display: flex;
		align-items: center;
		gap: 16px;
		margin-bottom: 16px;
	}

	.g-header-info-card-edit__cover {
		position: relative;
		flex-shrink: 0;
		box-sizing: border-box;
		border-style: solid;
		overflow: hidden;
		background: #fafafa;
	}

	.g-header-info-card-edit__cover-img {
		display: block;
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	.g-header-info-card-edit__cover-empty {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 100%;
		height: 100%;
		font-size: 32px;
		color: #bfbfbf;
	}

	.g-header-info-card-edit__text {
		min-width: 0;
		flex: 1;
	}

	.g-header-info-card-edit__title-input,
	.g-header-info-card-edit__subtitle-input {
		display: block;
		width: 100%;
		border: none;
		background: transparent;
		outline: none;
	}

	.g-header-info-card-edit__title-input {
		font-size: 26px;
		font-weight: 700;
		line-height: 1.4;
	}

	.g-header-info-card-edit__subtitle-input {
		margin-top: 12px;
		font-size: 18px;
		font-weight: 400;
		line-height: 1.4;
	}

	.g-header-info-card-edit__panel {
		width: max-content;
		max-width: 100%;
		border: 1px solid #eceef2;
		border-radius: 4px;
		overflow: visible;
	}

	.g-header-info-card-edit__link-row {
		display: flex;
		align-items: center;
		gap: 8px;
		min-height: 40px;
		padding: 0 10px;
		border-bottom: 1px solid #f0f0f0;
		font-size: 13px;
		color: #666;

		i {
			color: #999;
		}

		.is-empty {
			color: #bbb;
		}
	}

	.g-header-info-card-edit__toolbar {
		display: flex;
		align-items: center;
		flex-wrap: nowrap;
		min-height: 40px;
		padding: 0 10px;
		width: max-content;
		max-width: 100%;
	}

	.g-header-info-card-edit__action {
		border: none;
		background: transparent;
		padding: 0 8px;
		font-size: 13px;
		color: #285af9;
		cursor: pointer;
		line-height: 32px;
		white-space: nowrap;
		flex-shrink: 0;

		&:hover:not(:disabled) {
			color: #1d4ed8;
		}
	}

	.g-header-info-card-edit__colors {
		display: flex;
		align-items: center;
		gap: 8px;
		padding-left: 8px;
	}

	.g-header-info-card-edit__swatch {
		position: relative;
		width: 18px;
		height: 18px;
		border: 1px solid #d9d9d9;
		border-radius: 2px;
		cursor: pointer;
		flex-shrink: 0;
		overflow: hidden;
	}

	.g-header-info-card-edit__color-input {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		padding: 0;
		border: none;
		opacity: 0;
		cursor: pointer;
	}

	.g-header-info-card-edit__file-input {
		display: none;
	}

	.g-header-info-card-edit__footer-btn {
		min-width: 72px;
		height: 32px;
		padding: 0 16px;
		border: 1px solid #d9d9d9;
		border-radius: 4px;
		background: #fff;
		font-size: 14px;
		color: #666;
		cursor: pointer;

		&.is-confirm {
			border-color: #285af9;
			background: #285af9;
			color: #fff;
		}

		& + & {
			margin-left: 10px;
		}
	}
</style>
